/**
 * Cave pictures for the menus, built once per cave, level and size (building a cave every frame
 * would be wasteful), fitted into a box.
 */

import { buildCave, mapSize } from '../../caves';
import type { CaveDef } from '../../caves/types';
import { BLACK } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { thumbnail } from '../../render/thumbnail';
import type { Rect } from './menuModel';
import { FAINT } from './theme';

const cache = new Map<string, HTMLCanvasElement | null>();

/** Whole pixels per cell for a w x h cave to fit maxW x maxH, at least 1. Pure. */
export function fitScale(w: number, h: number, maxW: number, maxH: number): number {
  if (w <= 0 || h <= 0) return 1;
  return Math.max(1, Math.floor(Math.min(maxW / w, maxH / h)));
}

function cacheKey(def: CaveDef, level: number, px: number, key?: string): string {
  return `${key ?? `${def.id}|${def.name}|${def.map.length}`}|${level}|${px}`;
}

/** The picture of `def` at `level`, `px` pixels per cell; null if the cave cannot be built. */
export function caveThumb(def: CaveDef, level: number, px: number, key?: string): HTMLCanvasElement | null {
  const k = cacheKey(def, level, px, key);
  const hit = cache.get(k);
  if (hit !== undefined) return hit;
  let canvas: HTMLCanvasElement | null = null;
  try {
    canvas = thumbnail(buildCave(def, level), px);
  } catch {
    canvas = null;
  }
  cache.set(k, canvas);
  return canvas;
}

/** Whether the picture for this box is already made (screens with many make a few per frame). */
export function thumbReady(def: CaveDef, level: number, box: Rect, key?: string): boolean {
  const { w, h } = mapSize(def);
  return cache.has(cacheKey(def, level, fitScale(w, h, box.w, box.h), key));
}

/** Draw the cave centred in `box` (shrunk if even one pixel per cell is too big); returns where it went. */
export function drawCaveThumb(screen: Screen, def: CaveDef, level: number, box: Rect, key?: string): Rect {
  const { w, h } = mapSize(def);
  const px = fitScale(w, h, box.w, box.h);
  const canvas = caveThumb(def, level, px, key);
  if (!canvas) {
    screen.fill(box.x, box.y, box.w, box.h, BLACK);
    screen.box(box.x, box.y, box.w, box.h, FAINT);
    screen.text('?', box.x + box.w / 2, box.y + box.h / 2 - 4, FAINT, 'center');
    return box;
  }
  let dw = canvas.width;
  let dh = canvas.height;
  if (dw > box.w || dh > box.h) {
    const f = Math.min(box.w / dw, box.h / dh);
    dw = Math.max(1, Math.floor(dw * f));
    dh = Math.max(1, Math.floor(dh * f));
  }
  const x = box.x + Math.floor((box.w - dw) / 2);
  const y = box.y + Math.floor((box.h - dh) / 2);
  screen.ctx.drawImage(canvas, x, y, dw, dh);
  return { x, y, w: dw, h: dh };
}
